// Profielfoto opnieuw opmaken voor de polaroid: de effen CP-achtergrond van de
// uitgeknipte foto vervangen door een andere CP-kleur, optioneel met een
// Storytelling Shape achter de persoon. Werkt in de browser (canvas).
// Achtergrond = de kleur die aan de randen het meest voorkomt; vanaf de randen
// "vollopen" (flood fill), zodat dezelfde kleur in kleding/gezicht blijft staan.
import { CP_STORY_VIERKANT } from "./cpVormen";

// Foto's uit de online opslag (nieuwe collega's) mag de browser niet uitlezen;
// via de eigen server (/api/foto) wel
export const viaEigenServer = (src) =>
  /^https:\/\/(firebasestorage\.googleapis\.com|storage\.googleapis\.com|[a-z0-9-]+\.firebasestorage\.app)\//i.test(src || "")
    ? `/api/foto?url=${encodeURIComponent(src)}`
    : src;

const laadBeeld = (src0) =>
  new Promise((resolve, reject) => {
    const src = viaEigenServer(src0);
    const img = new Image();
    if (/^https?:/.test(src)) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const hexNaarRgb = (h) => {
  const m = String(h).replace("#", "");
  return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
};

// Masker (0 = achtergrond, 1 = persoon) op de oorspronkelijke resolutie
const maakMasker = (data, w, h) => {
  // Achtergrondkleur: meest voorkomende (afgeronde) randkleur
  const tel = {};
  const rand = [];
  for (let x = 0; x < w; x++) rand.push(x, (h - 1) * w + x);
  for (let y = 0; y < h; y++) rand.push(y * w, y * w + w - 1);
  rand.forEach((i) => {
    const k = `${data[i * 4] >> 3},${data[i * 4 + 1] >> 3},${data[i * 4 + 2] >> 3}`;
    tel[k] = (tel[k] || 0) + 1;
  });
  const [kb] = Object.entries(tel).sort((a, b) => b[1] - a[1])[0];
  const deel = kb.split(",").map((v) => Number(v) * 8 + 4);
  // Gemiddelde echte kleur van die randpixels
  let n = 0;
  const bg = [0, 0, 0];
  rand.forEach((i) => {
    const k = `${data[i * 4] >> 3},${data[i * 4 + 1] >> 3},${data[i * 4 + 2] >> 3}`;
    if (k === kb) {
      bg[0] += data[i * 4];
      bg[1] += data[i * 4 + 1];
      bg[2] += data[i * 4 + 2];
      n++;
    }
  });
  if (n) for (let c = 0; c < 3; c++) bg[c] /= n;
  else bg.splice(0, 3, ...deel);

  const afst = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const dr = data[i * 4] - bg[0];
    const dg = data[i * 4 + 1] - bg[1];
    const db = data[i * 4 + 2] - bg[2];
    afst[i] = Math.sqrt(dr * dr + dg * dg + db * db);
  }
  const T1 = 52; // zeker achtergrond (vanaf de rand)
  const alpha = new Float32Array(w * h).fill(1);
  const buren = (i) => {
    const x = i % w;
    const y = (i / w) | 0;
    return [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, y > 0 ? i - w : -1, y < h - 1 ? i + w : -1];
  };
  // A. Vollopen vanaf de randen
  const stapel = [];
  rand.forEach((i) => {
    if (alpha[i] === 1 && afst[i] < T1) {
      alpha[i] = 0;
      stapel.push(i);
    }
  });
  while (stapel.length) {
    const i = stapel.pop();
    for (const j of buren(i)) {
      if (j >= 0 && alpha[j] === 1 && afst[j] < T1) {
        alpha[j] = 0;
        stapel.push(j);
      }
    }
  }
  // B. Ingesloten stukjes oude achtergrond (tussen arm en lijf, tussen haarlokken):
  //    kern die echt de oude kleur is (< 30), uitgebreid met wat er sterk op lijkt (< 58)
  const KERN = 38;
  const GROEI = 70;
  const bekeken = new Uint8Array(w * h);
  for (let s0 = 0; s0 < w * h; s0++) {
    if (bekeken[s0] || alpha[s0] === 0 || afst[s0] >= KERN) continue;
    const deel = [s0];
    let kern = 0;
    bekeken[s0] = 1;
    for (let k = 0; k < deel.length; k++) {
      if (afst[deel[k]] < KERN) kern++;
      for (const j of buren(deel[k]))
        if (j >= 0 && !bekeken[j] && alpha[j] !== 0 && afst[j] < GROEI) {
          bekeken[j] = 1;
          deel.push(j);
        }
    }
    if (kern >= 3) deel.forEach((i) => (alpha[i] = 0));
  }
  // C. Randzone (tot 3 pixels van de achtergrond): half doorzichtig + kleur ontsmetten
  const stap = new Uint8Array(w * h); // 0 = geen rand, 1..DIEP = afstand tot achtergrond
  let rij = [];
  for (let i = 0; i < w * h; i++) if (alpha[i] === 0) rij.push(i);
  const DIEP = Math.max(6, Math.round(w / 40)); // zachte gloed is breder bij grote foto's
  for (let r = 1; r <= DIEP; r++) {
    const volgende = [];
    for (const i of rij)
      for (const j of buren(i))
        if (j >= 0 && alpha[j] > 0 && !stap[j]) {
          stap[j] = r;
          volgende.push(j);
        }
    rij = volgende;
  }
  const TZACHT = [0, 185, 150, 115];
  // Resten van een éérdere CP-achtergrond langs de rand (oude uitsnede): ook weg
  const CP_LICHT = [[177, 237, 166], [157, 180, 246], [255, 105, 155], [255, 202, 85]];
  for (let i = 0; i < w * h; i++) {
    if (!stap[i] || stap[i] > 3) continue;
    for (const c of CP_LICHT) {
      const dr = data[i * 4] - c[0];
      const dg = data[i * 4 + 1] - c[1];
      const db = data[i * 4 + 2] - c[2];
      if (dr * dr + dg * dg + db * db < 62 * 62) {
        alpha[i] = 0;
        break;
      }
    }
  }
  for (let i = 0; i < w * h; i++) {
    const r = stap[i];
    if (!r || r > 3) continue;
    alpha[i] = Math.max(0, Math.min(alpha[i], (afst[i] - T1) / (TZACHT[r] - T1)));
  }
  // Schaduw van de oude uitsnede: dezelfde kleur als de achtergrond, alleen donkerder
  // (r/g/b in dezelfde verhouding). Vanaf de achtergrond naar binnen weghalen.
  const isSchaduw = (i) => {
    const q = [data[i * 4] / (bg[0] || 1), data[i * 4 + 1] / (bg[1] || 1), data[i * 4 + 2] / (bg[2] || 1)];
    const k = (q[0] + q[1] + q[2]) / 3;
    return k > 0.42 && k < 1.02 && Math.max(Math.abs(q[0] - k), Math.abs(q[1] - k), Math.abs(q[2] - k)) < 0.07;
  };
  {
    let golf = [];
    for (let i = 0; i < w * h; i++) if (alpha[i] === 0) golf.push(i);
    for (let r = 0; r < DIEP * 4 && golf.length; r++) {
      const volgende = [];
      for (const i of golf)
        for (const j of buren(i))
          if (j >= 0 && alpha[j] > 0 && isSchaduw(j)) {
            alpha[j] = 0;
            volgende.push(j);
          }
      golf = volgende;
    }
  }
  // Zachte gloed/veer langs de uitsnede: pixel = mengsel van persoon (F) en oude achtergrond.
  // F = kleur van de persoon net binnen de rand; doorzichtigheid = waar de pixel op de lijn
  // achtergrond → F ligt. Alleen als de pixel echt op die lijn ligt (geen eigen kleur).
  const F = new Float32Array(w * h * 3);
  for (let i = 0; i < w * h; i++) if (alpha[i] > 0 && !stap[i]) for (let k = 0; k < 3; k++) F[i * 3 + k] = data[i * 4 + k];
  const heeftF = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) if (alpha[i] > 0 && !stap[i]) heeftF[i] = 1;
  for (let r = DIEP; r >= 1; r--)
    for (let i = 0; i < w * h; i++) {
      if (stap[i] !== r) continue;
      let n = 0;
      const som = [0, 0, 0];
      for (const j of buren(i))
        if (j >= 0 && heeftF[j] && (stap[j] === 0 || stap[j] > r)) {
          for (let k = 0; k < 3; k++) som[k] += F[j * 3 + k];
          n++;
        }
      if (!n) continue;
      for (let k = 0; k < 3; k++) F[i * 3 + k] = som[k] / n;
      heeftF[i] = 1;
    }
  for (let i = 0; i < w * h; i++) {
    if (!stap[i] || !heeftF[i] || alpha[i] === 0) continue;
    const fb = [F[i * 3] - bg[0], F[i * 3 + 1] - bg[1], F[i * 3 + 2] - bg[2]];
    const len2 = fb[0] * fb[0] + fb[1] * fb[1] + fb[2] * fb[2];
    if (len2 < 70 * 70) continue; // persoon lijkt te veel op de achtergrond: niet te bepalen
    const cb = [data[i * 4] - bg[0], data[i * 4 + 1] - bg[1], data[i * 4 + 2] - bg[2]];
    const t = (cb[0] * fb[0] + cb[1] * fb[1] + cb[2] * fb[2]) / len2;
    if (t >= 0.92) continue;
    // Afstand van de pixel tot de lijn: groot = eigen kleur (geen gloed), dan niets doen
    const rest = Math.sqrt(
      (cb[0] - t * fb[0]) ** 2 + (cb[1] - t * fb[1]) ** 2 + (cb[2] - t * fb[2]) ** 2
    );
    if (rest > 38) continue;
    const a = Math.max(0, Math.min(1, (t - 0.12) / 0.8));
    if (a < alpha[i]) alpha[i] = a;
    for (let k = 0; k < 3; k++) data[i * 4 + k] = F[i * 3 + k];
  }
  // Kleurrand van de oude achtergrond eruit (ook bij pixels die wel blijven staan)
  for (let i = 0; i < w * h; i++) {
    const r = stap[i];
    if (!r || alpha[i] === 0) continue;
    // Dichtbij de rand sterk; dieper (dunne haarlokjes) alleen pixels met dezelfde kleurtoon als de oude achtergrond
    if (r > 3) {
      const gp = (data[i * 4] + data[i * 4 + 1] + data[i * 4 + 2]) / 3;
      const gb = (bg[0] + bg[1] + bg[2]) / 3;
      let dot = 0;
      let np = 0;
      let nb = 0;
      for (let k = 0; k < 3; k++) {
        const vp = data[i * 4 + k] - gp;
        const vb = bg[k] - gb;
        dot += vp * vb;
        np += vp * vp;
        nb += vb * vb;
      }
      if (!np || !nb || dot / Math.sqrt(np * nb) < 0.93 || afst[i] > 110) continue;
    }
    const a = Math.max(0.35, Math.min(1, (afst[i] - T1) / ((r <= 3 ? 230 : 130) - T1)));
    if (a >= 1) continue;
    for (let k = 0; k < 3; k++) data[i * 4 + k] = Math.max(0, Math.min(255, (data[i * 4 + k] - (1 - a) * bg[k]) / a));
  }
  // Dun lijntje langs de uitsnede (zit soms al in de originele foto): de buitenste
  // 3 pixels krijgen de kleur van de pixels er net binnen
  for (let r = 3; r >= 1; r--)
    for (let i = 0; i < w * h; i++) {
      if (stap[i] !== r || alpha[i] === 0) continue;
      let n = 0;
      const som = [0, 0, 0];
      for (const j of buren(i))
        if (j >= 0 && alpha[j] > 0 && (stap[j] === 0 || stap[j] > r)) {
          for (let k = 0; k < 3; k++) som[k] += data[j * 4 + k];
          n++;
        }
      if (n) for (let k = 0; k < 3; k++) data[i * 4 + k] = som[k] / n;
      if (r === 1) alpha[i] = Math.min(alpha[i], 0.6);
    }
  // Zachte rand: 3x3 vervaging, alleen naar beneden (persoon niet aantasten)
  const zacht = new Float32Array(alpha);
  for (let y = 1; y < h - 1; y++)
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      if (!stap[i] && alpha[i] === 1) continue;
      let som = 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) som += alpha[i + dy * w + dx];
      zacht[i] = Math.min(alpha[i], som / 9);
    }
  return { alpha: zacht, bg };
};

const cache = new Map();

// Oude profielfoto's (200 px) waarvan de automatische uitsnede lelijk werd: vooraf
// netjes uitgeknipt (transparant). Sleutel = lengte + laatste 40 tekens van de foto.
const VRIJ_UITSNEDE = {
  "7883:H2pgGts0gNGvRQ0RamlX6ZvdNWKPywiImB8//9k=": "/profielfotos-vrij/quincy.webp",
};

// Persoon vrijstaand (transparante achtergrond) als canvas; null als het niet lukt
const vrijCache = new Map();
export const vrijstaandCanvas = (src) => {
  if (!src) return Promise.resolve(null);
  const k = `${src.length}:${src.slice(-40)}`;
  if (!vrijCache.has(k))
    vrijCache.set(
      k,
      (async () => {
        const vrijUrl = VRIJ_UITSNEDE[`${src.length}:${src.slice(-40)}`];
        const img = await laadBeeld(vrijUrl || src);
        const zijde = Math.min(img.naturalWidth || img.width, img.naturalHeight || img.height);
        // Kleine foto's (oude profielfoto's van 200 px) eerst vergroten: dan wordt de
        // uitsnede vloeiend in plaats van blokkerig
        const w = Math.max(Math.min(zijde, 700), 600);
        const h = w;
        const c0 = document.createElement("canvas");
        c0.width = w;
        c0.height = h;
        const x0 = c0.getContext("2d", { willReadFrequently: true });
        const sx = ((img.naturalWidth || img.width) - zijde) / 2;
        const sy = ((img.naturalHeight || img.height) - zijde) / 2;
        x0.imageSmoothingQuality = "high";
        x0.drawImage(img, sx, sy, zijde, zijde, 0, 0, w, h);
        const beeld = x0.getImageData(0, 0, w, h);
        const d = beeld.data;
        // Al vrijstaand (transparante PNG van de profielfoto-maker)? Dan het eigen masker gebruiken
        let doorzichtig = 0;
        let randN = 0;
        for (let i = 0; i < w; i += 4) {
          randN += 2;
          if (d[i * 4 + 3] < 200) doorzichtig++;
          if (d[((h - 1) * w + i) * 4 + 3] < 200) doorzichtig++;
        }
        const alpha = new Float32Array(w * h);
        if (vrijUrl || doorzichtig > randN * 0.3) for (let i = 0; i < w * h; i++) alpha[i] = d[i * 4 + 3] / 255;
        else alpha.set(maakMasker(d, w, h).alpha);
        for (let i = 0; i < w * h; i++) d[i * 4 + 3] = Math.round(alpha[i] * 255);
        x0.putImageData(beeld, 0, 0);
        return c0;
      })().catch(() => null)
    );
  return vrijCache.get(k);
};

/**
 * @param {string} src  dataURL of http-URL van de profielfoto
 * @param {{achtergrond?: string, vorm?: string|null, vormKleur?: string, maat?: number}} opt
 * @returns {Promise<string|null>} dataURL (jpeg) of null als het niet lukt (bijv. CORS)
 */
export const opgemaakteFoto = async (src, { achtergrond, vorm = null, vormKleur, maat = 900 } = {}) => {
  if (!src || !achtergrond) return src || null;
  const sleutel = `${src.length}:${src.slice(-40)}|${achtergrond}|${vorm}|${vormKleur}|${maat}`;
  if (cache.has(sleutel)) return cache.get(sleutel);
  try {
    const c0 = await vrijstaandCanvas(src);
    if (!c0) throw new Error("geen uitsnede");

    const c = document.createElement("canvas");
    c.width = c.height = maat;
    const x = c.getContext("2d");
    const [r, g, b] = hexNaarRgb(achtergrond);
    x.fillStyle = `rgb(${r},${g},${b})`;
    x.fillRect(0, 0, maat, maat);
    if (vorm && CP_STORY_VIERKANT[vorm] && vormKleur) {
      const v = CP_STORY_VIERKANT[vorm];
      // Vorm onderaan gecentreerd, 82% van de breedte, nooit uitgerekt
      const s = (maat * 0.82) / v.w;
      x.save();
      x.translate((maat - v.w * s) / 2, maat - v.h * s);
      x.scale(s, s);
      x.fillStyle = vormKleur;
      x.fill(new Path2D(v.d));
      x.restore();
    }
    x.imageSmoothingQuality = "high";
    x.drawImage(c0, 0, 0, maat, maat);
    const uit = c.toDataURL("image/jpeg", 0.9);
    cache.set(sleutel, uit);
    return uit;
  } catch (e) {
    cache.set(sleutel, null);
    return null;
  }
};
